import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { X, Menu, ShoppingBag, User, LogOut, Download, ChevronDown, LayoutDashboard, ArrowLeft, Send, Share2, Link2, ChevronLeft, ChevronRight, BookOpen } from 'lucide-react';

// ─── SEO META PER PAGE ───────────────────────────────────────────
const SEO_META = {
    '/': {
        title: 'Know Your Stuff — A Reference Book of Social and Business Etiquette | Alice M. White, B.S., MBA',
        description: 'Know Your Stuff by Alice M. White, B.S., MBA features 100+ timeless tips on business etiquette, social etiquette, networking, professional image, dining, and interviewing. Available as a physical book and e-book at knowyourstuffbook.com.',
        canonical: 'https://www.knowyourstuffbook.com/',
    },
    '/about-the-book': {
        title: 'About the Book — Know Your Stuff: A Reference Book of Social and Business Etiquette',
        description: 'Know Your Stuff is a thoughtfully crafted reference guide covering grammar, office communications, business etiquette, dining, professional image, interviewing skills, time management, and more. Written by Alice M. White, B.S., MBA.',
        canonical: 'https://www.knowyourstuffbook.com/about-the-book',
    },
    '/about-the-author': {
        title: 'About Alice M. White, B.S., MBA — Author of Know Your Stuff',
        description: 'Alice M. White is an accomplished author, educator, and etiquette authority with a B.S. and MBA. She is the author of Know Your Stuff: A Reference Book of Social and Business Etiquette, published by Lee\'s Press.',
        canonical: 'https://www.knowyourstuffbook.com/about-the-author',
    },
    '/shop': {
        title: 'Shop — Buy Know Your Stuff: Physical Book & E-Book | Alice M. White',
        description: 'Purchase Know Your Stuff by Alice M. White, B.S., MBA. Available as a physical paperback (ISBN 9798271826030) or instant-access e-book. Secure checkout via PayPal at knowyourstuffbook.com.',
        canonical: 'https://www.knowyourstuffbook.com/shop',
    },
    '/ebook': {
        title: 'Read Know Your Stuff E-Book Online | Alice M. White, B.S., MBA',
        description: 'Read Know Your Stuff: A Reference Book of Social and Business Etiquette by Alice M. White directly on the platform. Create an account, purchase, and access the full 224-page e-book instantly. No download required.',
        canonical: 'https://www.knowyourstuffbook.com/ebook',
    },
    '/account': {
        title: 'My Account — Know Your Stuff Book Platform',
        description: 'Access your purchases, downloads, and e-book library on the Know Your Stuff platform by Alice M. White.',
        canonical: 'https://www.knowyourstuffbook.com/account',
    },
    '/contact': {
        title: 'Contact — Know Your Stuff Book | Alice M. White',
        description: 'Get in touch with Alice M. White or the Know Your Stuff Book team. Questions about the book, speaking engagements, or business inquiries.',
        canonical: 'https://www.knowyourstuffbook.com/contact',
    },
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BOOK_COVER = 'https://customer-assets.emergentagent.com/job_8519ff1f-a56c-4ad1-bdd3-9bc47b61a225/artifacts/9dafrk4v_IMG_0654.jpeg';
const BOOK_BACK = 'https://customer-assets.emergentagent.com/job_8519ff1f-a56c-4ad1-bdd3-9bc47b61a225/artifacts/popn42cp_IMG_0655.jpeg';

// ─── NAV ────────────────────────────────────────────────────────
function Navbar({ user, onLogout, onLoginClick }) {
    const [menuOpen, setMenuOpen] = useState(false);
    const scrollTo = (id) => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
        setMenuOpen(false);
    };
    return (
        <nav className="fixed top-0 left-0 right-0 z-50 bg-navy/95 backdrop-blur border-b border-gold/30" data-testid="navbar">
            <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                <button onClick={() => scrollTo('hero')} className="font-heading text-gold text-xl md:text-2xl font-bold tracking-wide hover:opacity-80 transition-opacity" data-testid="nav-logo">
                    KNOW YOUR STUFF
                </button>
                {/* Desktop Nav */}
                <div className="hidden md:flex items-center gap-8">
                    {[['about-book','About the Book'],['about-author','About the Author'],['shop','Shop'],['contact','Contact']].map(([id,label])=>(
                        <button key={id} onClick={()=>scrollTo(id)} className="font-body text-white/90 hover:text-gold transition-colors text-base" data-testid={`nav-${id}-link`}>{label}</button>
                    ))}
                    {user && (
                        <button onClick={()=>scrollTo('account')} className="font-body text-white/90 hover:text-gold transition-colors text-base" data-testid="nav-account-link">My Account</button>
                    )}
                    {user?.role === 'admin' && (
                        <a href="/admin" className="font-body flex items-center gap-2 bg-gold/20 border border-gold/50 text-gold hover:bg-gold hover:text-navy px-4 py-2 rounded font-bold text-sm transition-colors min-h-[44px]" data-testid="nav-admin-link">
                            <LayoutDashboard size={15}/> Admin Panel
                        </a>
                    )}
                    {user ? (
                        <button onClick={onLogout} className="font-body flex items-center gap-2 text-white/90 hover:text-gold transition-colors text-base" data-testid="nav-logout-btn">
                            <LogOut size={16}/> Logout
                        </button>
                    ) : (
                        <button onClick={onLoginClick} className="bg-gold hover:bg-gold-hover text-navy font-bold px-5 py-2 rounded font-body transition-colors min-h-[44px]" data-testid="nav-login-btn">Login</button>
                    )}
                </div>
                {/* Mobile Hamburger */}
                <button className="md:hidden text-gold" onClick={()=>setMenuOpen(!menuOpen)} data-testid="nav-hamburger-btn">
                    {menuOpen ? <X size={28}/> : <Menu size={28}/>}
                </button>
            </div>
            {/* Mobile Menu */}
            {menuOpen && (
                <div className="md:hidden bg-navy border-t border-gold/20 px-6 py-4 flex flex-col gap-4" data-testid="mobile-menu">
                    {[['about-book','About the Book'],['about-author','About the Author'],['shop','Shop'],['contact','Contact']].map(([id,label])=>(
                        <button key={id} onClick={()=>scrollTo(id)} className="font-body text-white text-lg py-3 text-left border-b border-gold/10">{label}</button>
                    ))}
                    {user && <button onClick={()=>scrollTo('account')} className="font-body text-white text-lg py-3 text-left border-b border-gold/10">My Account</button>}
                    {user ? (
                        <button onClick={()=>{onLogout();setMenuOpen(false);}} className="font-body text-white text-lg py-3 text-left flex items-center gap-2"><LogOut size={18}/>Logout</button>
                    ) : (
                        <button onClick={()=>{onLoginClick();setMenuOpen(false);}} className="bg-gold text-navy font-bold py-3 px-6 rounded font-body text-lg min-h-[44px]">Login / Register</button>
                    )}
                </div>
            )}
        </nav>
    );
}

// ─── HERO ────────────────────────────────────────────────────────
function HeroSection() {
    const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    return (
        <section id="hero" className="min-h-screen bg-navy flex items-center pt-20" data-testid="hero-section">
            <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 grid grid-cols-1 md:grid-cols-2 gap-12 items-center w-full">
                <div className="space-y-6 order-2 md:order-1 animate-fade-in">
                    <p className="font-body text-gold text-sm uppercase tracking-[0.3em]">Alice M. White, B.S., MBA</p>
                    <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl text-white font-bold leading-tight" data-testid="hero-h1">
                        Know Your Stuff <span className="block text-gold">— A Reference Book of Social and Business Etiquette</span>
                    </h1>
                    <p className="font-body text-lg text-white/70">100+ Timeless Tips for Everyday Success</p>
                    <div className="flex flex-wrap gap-3 pt-2">
                        {['Networking','Social Etiquette','Business Etiquette','Professional Development','Interviewing Skills','Dining Etiquette','Professional Image'].map(tag=>(
                            <span key={tag} className="border border-gold/50 text-gold text-sm font-body px-3 py-1 rounded-sm">{tag}</span>
                        ))}
                    </div>
                    <p className="font-body text-base text-white/50">Available as a physical book and e-book. Published by <span className="text-gold/70">Lee's Press</span>. ISBN 9798271826030.</p>
                    <div className="pt-4">
                        <button onClick={()=>scrollTo('shop')} className="bg-gold hover:bg-gold-hover text-navy font-bold px-10 py-4 rounded-sm font-body text-lg transition-colors min-h-[56px]" data-testid="hero-shop-btn">
                            Purchase My Timeless Handbook
                        </button>
                    </div>
                </div>
                <div className="flex justify-center order-1 md:order-2 animate-fade-in">
                    <div className="relative">
                        <img src={BOOK_COVER} alt="Know Your Stuff book cover — A Reference Book of Social and Business Etiquette by Alice M. White" className="w-64 md:w-80 lg:w-96 rounded shadow-2xl shadow-gold/20 object-cover" width="384" height="576" data-testid="hero-book-cover"/>
                        <div className="absolute -bottom-4 -right-4 bg-gold text-navy rounded-full w-24 h-24 flex items-center justify-center text-center font-body font-bold text-xs leading-tight shadow-lg">
                            <span>100+<br/>Timeless<br/>Tips</span>
                        </div>
                    </div>
                </div>
            </div>
            {/* SEO keyword anchors - visible to crawlers */}
            <p className="sr-only">knowyourstuffbook.com — Know Your Stuff by Alice M. White, B.S., MBA. A Reference Book of Social and Business Etiquette featuring 100+ Timeless Tips for Everyday Success. Covering business etiquette, social etiquette, professional development, networking, interviewing skills, dining etiquette, and professional image. Available as a physical book and e-book. Published by Lee's Press. ISBN 9798271826030.</p>
        </section>
    );
}

// ─── ABOUT THE BOOK ──────────────────────────────────────────────
function AboutBookSection() {
    return (
        <section id="about-book" className="bg-ivory py-20 md:py-28" data-testid="about-book-section">
            <div className="max-w-4xl mx-auto px-6 md:px-12">
                <div className="text-center mb-12">
                    <h2 className="font-heading text-4xl md:text-5xl text-navy font-bold">About the Book</h2>
                    <div className="w-20 h-1 bg-gold mx-auto mt-4"></div>
                </div>
                <div className="space-y-6 font-body text-lg text-navy/80 leading-relaxed" data-testid="about-book-content">
                    <p>In a world that often feels hurried, disconnected, and uncertain, <em>Know Your Stuff: A Reference Book of Social and Business Etiquette</em> by Alice M. White offers a refreshing return to the foundational values of respect, poise, and professionalism. More than just a book of manners, this thoughtfully crafted guide is a practical companion for anyone seeking to navigate life's personal and professional relationships with confidence and integrity.</p>
                    <p>Rooted in Alice's wealth of accumulated knowledge, motivation, and natural giftedness in the area of human interaction, <em>Know Your Stuff</em> is the result of years of observing, practicing, and teaching the subtle yet powerful principles that shape how we relate to one another. Her intent is simple but profound: to provide a clear, accessible, and timeless reference for those moments when we all need a little guidance.</p>
                    <p>From job interviews and workplace communication to dining etiquette, social invitations, digital conduct, and everything in between, Alice provides practical tips and real-life insight that make etiquette approachable and relevant. She recognizes that while the world changes, there are core communication principles and cultural truths that remain constant. These types of foundations: respect, empathy, courtesy, and self-awareness are what she calls the true "stuff" you need to know to move successfully through modern society.</p>
                    <p>Whether you're a young professional entering the workforce, a student preparing for leadership roles, or someone who simply wants to feel more polished and prepared in daily interactions, this book will meet you where you are. It's written not to impress, but to empower — to be flipped through when a question arises or read cover to cover as a foundation for confident, considerate living.</p>
                    <p>With her signature warmth and clarity, Alice M. White reminds us that etiquette is not about perfection — it's about awareness, adaptability, and the ability to make others feel respected and valued.</p>
                    <p className="font-bold text-navy"><em>Know Your Stuff</em> is more than a reference. It's a tool for building stronger relationships, seizing opportunities, and showing up in the world with excellence.</p>
                </div>
                <div className="flex flex-wrap gap-3 mt-10 justify-center">
                    {['Networking','Social Etiquette','Business Etiquette','Professional Development','Interviewing Skills','Dining Etiquette','Professional Image','Office Communication','Grammar','Time Management'].map(tag=>(
                        <span key={tag} className="border-2 border-gold text-navy bg-white font-body font-bold px-5 py-2 rounded-sm text-base" data-testid={`topic-tag-${tag.toLowerCase().replace(/\s/g,'-')}`}>{tag}</span>
                    ))}
                </div>
            </div>
        </section>
    );
}

// ─── ABOUT THE AUTHOR ────────────────────────────────────────────
function AboutAuthorSection() {
    const [photos, setPhotos] = useState([]);
    const [videos, setVideos] = useState([]);
    useEffect(() => {
        axios.get(`${API}/about-photos`).then(r => setPhotos(r.data)).catch(() => {});
        axios.get(`${API}/about-videos`).then(r => setVideos(r.data)).catch(() => {});
    }, []);

    return (
        <section id="about-author" className="bg-navy py-20 md:py-28" data-testid="about-author-section">
            <div className="max-w-6xl mx-auto px-6 md:px-12">
                <div className="text-center mb-12">
                    <h2 className="font-heading text-4xl md:text-5xl text-gold font-bold">About the Author</h2>
                    <div className="w-20 h-1 bg-gold mx-auto mt-4"></div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
                    <div className="flex justify-center">
                        <img src={BOOK_BACK} alt="Know Your Stuff back cover with author bio" className="w-72 md:w-full max-w-sm rounded shadow-2xl shadow-gold/20" data-testid="author-image"/>
                    </div>
                    <div className="space-y-6">
                        <div>
                            <h3 className="font-heading text-2xl md:text-3xl text-white font-semibold">Alice M. White, B.S., MBA</h3>
                            <p className="font-body text-gold/80 text-base mt-1">Author, Educator, Etiquette Authority</p>
                        </div>
                        <div className="space-y-4 font-body text-lg text-white/80 leading-relaxed" data-testid="author-bio">
                            <p>Alice M. White is an accomplished author, educator, and etiquette authority with a Bachelor of Science degree and a Master of Business Administration (MBA). With years of professional experience across business, education, and human development, Alice has dedicated her career to helping individuals present their best selves in every room they enter.</p>
                            <p>Her passion for etiquette was born from a lifelong observation of the profound impact that manners, communication, and professionalism have on personal and professional success. Alice believes that confidence is built on preparation — and that knowing how to carry yourself, communicate effectively, and treat others with respect is one of the most valuable sets of skills a person can possess.</p>
                            <p><em>Know Your Stuff</em> is Alice's offering to anyone who has ever walked into a room and wondered if they were doing it right. Her expertise spans business etiquette, social etiquette, professional development, office communications, interviewing skills, dining etiquette, and professional image. Her approach is warm, practical, and deeply human — never judgmental, always empowering.</p>
                            <p>Alice M. White brings over decades of real-world experience to every page of this book, making it not just a reference guide, but a reflection of a life lived with intention, grace, and excellence.</p>
                        </div>
                        <div className="border-t border-gold/30 pt-6 space-y-1 font-body text-base text-white/60">
                            <p>Published by: <span className="text-gold font-semibold">Lee's Press — A Premiere Self-Publishing Services Company</span></p>
                            <p>ISBN: <span className="text-gold font-semibold">9798271826030</span></p>
                            <p>Book: <span className="text-gold font-semibold">Know Your Stuff: A Reference Book of Social and Business Etiquette</span></p>
                        </div>
                    </div>
                </div>
                {/* Author Personal Photos */}
                {photos.length > 0 && (
                    <div className="mt-16" data-testid="author-photos">
                        <h3 className="font-heading text-2xl text-gold font-semibold text-center mb-8">Meet Alice</h3>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                            {photos.map((p, i) => {
                                const gridSrc = p.url?.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${p.url}` : p.url;
                                const largeSrc = p.url_large?.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${p.url_large}` : (p.url_large || gridSrc);
                                const shape = p.display_shape || 'square';
                                const ratioCls = ({square:'aspect-square',landscape:'aspect-[3/2]',portrait:'aspect-[2/3]',original:'aspect-auto'})[shape];
                                const imgFit = shape === 'original' ? 'w-auto h-auto max-w-full mx-auto' : 'w-full h-full object-cover';
                                return (
                                    <figure key={p.photo_id} className="space-y-2" data-testid={`author-photo-figure-${i}`} data-shape={shape}>
                                        <a href={largeSrc} target="_blank" rel="noopener noreferrer" className={`block rounded-lg overflow-hidden shadow-lg shadow-gold/10 border border-gold/20 ${ratioCls} bg-navy/40 flex items-center justify-center`} data-testid={`author-photo-link-${i}`}>
                                            <img src={gridSrc} alt={p.caption || `Alice M. White - Photo ${i + 1}`} className={`${imgFit} hover:scale-105 transition-transform duration-500`} loading="lazy" decoding="async" data-testid={`author-photo-${i}`}/>
                                        </a>
                                        {p.caption && (
                                            <figcaption className="font-body text-sm text-white/70 text-center italic px-1" data-testid={`author-photo-caption-${i}`}>{p.caption}</figcaption>
                                        )}
                                    </figure>
                                );
                            })}
                        </div>
                    </div>
                )}
                {/* Author Personal Videos */}
                {videos.length > 0 && (
                    <div className="mt-16" data-testid="author-videos">
                        <h3 className="font-heading text-2xl text-gold font-semibold text-center mb-8">Hear from Alice</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {videos.map((v, i) => {
                                const src = v.url?.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${v.url}` : v.url;
                                return (
                                    <figure key={v.video_id} className="space-y-2">
                                        <div className="rounded-lg overflow-hidden shadow-lg shadow-gold/10 border border-gold/20 bg-black aspect-video">
                                            <video src={src} controls playsInline preload="metadata" className="w-full h-full object-contain" data-testid={`author-video-${i}`}/>
                                        </div>
                                        {v.caption && (
                                            <figcaption className="font-body text-sm text-white/70 text-center italic px-1" data-testid={`author-video-caption-${i}`}>{v.caption}</figcaption>
                                        )}
                                    </figure>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
}

// ─── PRODUCT DETAIL MODAL ────────────────────────────────────────
function ProductDetailModal({ product, onClose, onBuyNow, onViewFullPage }) {
    const [activeImg, setActiveImg] = useState(0);
    const images = [
        product.display_url || (product.image_path ? `${API}/files/${product.image_path}` : BOOK_COVER),
        ...(product.additional_urls || (product.additional_images || []).map(p => `${API}/files/${p}`))
    ];
    const productUrl = `${window.location.origin}/product/${product.product_id}`;
    const shareOnFb = () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(productUrl)}`, '_blank');
    const shareOnX = () => window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(productUrl)}&text=${encodeURIComponent(product.title)}`, '_blank');
    const copyLink = () => navigator.clipboard.writeText(productUrl);

    return (
        <div className="fixed inset-0 bg-navy/80 backdrop-blur flex items-center justify-center z-50 p-4" data-testid="product-detail-modal">
            <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto relative">
                <button onClick={onClose} className="absolute top-4 right-4 text-navy/40 hover:text-navy z-10 bg-white/80 rounded-full p-1" data-testid="product-detail-close-btn"><X size={24}/></button>
                <div className="grid grid-cols-1 md:grid-cols-2">
                    {/* Image Gallery */}
                    <div className="bg-navy flex flex-col items-center justify-center p-6 min-h-[420px] relative">
                        <div className="aspect-[2/3] w-full max-w-[260px] flex items-center justify-center">
                            <img src={images[activeImg]} alt={product.title} className="max-w-full max-h-full w-auto h-auto object-contain rounded shadow-lg" data-testid="product-detail-image"/>
                        </div>
                        {images.length > 1 && (
                            <>
                                <button onClick={()=>setActiveImg(i=>(i-1+images.length)%images.length)} className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-navy rounded-full w-8 h-8 flex items-center justify-center shadow" data-testid="modal-gallery-prev"><ChevronLeft size={16}/></button>
                                <button onClick={()=>setActiveImg(i=>(i+1)%images.length)} className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-navy rounded-full w-8 h-8 flex items-center justify-center shadow" data-testid="modal-gallery-next"><ChevronRight size={16}/></button>
                                <div className="flex gap-2 mt-3">
                                    {images.map((src, i) => (
                                        <button key={i} onClick={()=>setActiveImg(i)} className={`w-12 h-12 rounded border-2 overflow-hidden transition-all bg-white/5 ${activeImg===i?'border-gold':'border-white/30 hover:border-gold/50'}`} data-testid={`modal-thumb-${i}`}>
                                            <img src={src} alt={`${i+1}`} className="w-full h-full object-contain"/>
                                        </button>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                    {/* Info */}
                    <div className="p-8 flex flex-col">
                        <span className={`text-xs font-body font-bold px-3 py-1 rounded-sm w-fit mb-3 ${product.type==='ebook'?'bg-gold text-navy':'bg-ivory text-navy border border-navy/20'}`}>
                            {product.type==='ebook'?'E-Book / Digital Download':'Physical Book'}
                        </span>
                        <h2 className="font-heading text-2xl md:text-3xl font-bold text-navy mb-4" data-testid="product-detail-title">{product.title}</h2>
                        {product.description && (
                            <p className="font-body text-navy/70 text-base leading-relaxed mb-4 flex-1" data-testid="product-detail-description">
                                {product.description.length > 300 ? product.description.slice(0,300)+'...' : product.description}
                            </p>
                        )}
                        {/* Share */}
                        <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-100">
                            <span className="font-body text-navy/50 text-sm">Share:</span>
                            <button onClick={shareOnFb} className="text-navy/40 hover:text-blue-600 transition-colors" data-testid="modal-share-fb" title="Facebook">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                            </button>
                            <button onClick={shareOnX} className="text-navy/40 hover:text-black transition-colors" data-testid="modal-share-x" title="X / Twitter">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                            </button>
                            <button onClick={copyLink} className="text-navy/40 hover:text-gold transition-colors" data-testid="modal-share-link" title="Copy link">
                                <Link2 size={18}/>
                            </button>
                        </div>
                        <div className="mt-auto space-y-3">
                            <div className="flex items-center justify-between mb-2">
                                <span className="font-heading text-3xl font-bold text-gold" data-testid="product-detail-price">${parseFloat(product.price).toFixed(2)}</span>
                            </div>
                            <button onClick={(e)=>{e.stopPropagation();onBuyNow(product);}} className="w-full bg-gold hover:bg-gold-hover text-navy font-bold px-6 py-4 rounded-sm font-body text-lg transition-colors min-h-[56px]" data-testid={`buy-now-btn-${product.product_id}`}>
                                Buy Now
                            </button>
                            <button onClick={onViewFullPage} className="w-full border-2 border-navy/20 hover:border-gold text-navy font-bold px-6 py-3 rounded-sm font-body text-base transition-colors min-h-[44px]" data-testid="view-full-page-btn">
                                View Full Product Page
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ─── SHOP ────────────────────────────────────────────────────────
function ShopSection() {
    const { user, setUser } = useAuth();
    const navigate = useNavigate();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    // Product detail modal
    const [selectedProduct, setSelectedProduct] = useState(null);
    // Checkout state
    const [checkoutProduct, setCheckoutProduct] = useState(null);
    const [checkoutData, setCheckoutData] = useState(null);
    const [checkoutLoading, setCheckoutLoading] = useState(false);
    const [checkoutSuccess, setCheckoutSuccess] = useState(null);
    const [error, setError] = useState('');
    const [couponCode, setCouponCode] = useState('');
    const [couponResult, setCouponResult] = useState(null);
    // Auth gate state (shown when not logged in + Buy Now clicked)
    const [authGateProduct, setAuthGateProduct] = useState(null);
    const [authTab, setAuthTab] = useState('login');
    const [loginForm, setLoginForm] = useState({ email: '', password: '' });
    const [registerForm, setRegisterForm] = useState({ full_name: '', email: '', password: '', confirm_password: '' });
    const [authError, setAuthError] = useState('');
    const [authLoading, setAuthLoading] = useState(false);

    useEffect(() => {
        axios.get(`${API}/products`).then(r => setProducts(r.data)).catch(()=>{}).finally(()=>setLoading(false));
    }, []);

    // Handle PayPal return redirect
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const isPaypalReturn = params.get('paypal_return') === 'true';
        const isPaypalCancel = params.get('paypal_cancel') === 'true';
        const payerId = params.get('PayerID');

        // Handle cancel
        if (isPaypalCancel) {
            localStorage.removeItem('kys_pending_paypal');
            window.history.replaceState({}, '', window.location.pathname);
            return;
        }

        if (!isPaypalReturn || !user) return;
        const pending = localStorage.getItem('kys_pending_paypal');
        if (!pending) return;
        try {
            const orderData = JSON.parse(pending);
            localStorage.removeItem('kys_pending_paypal');
            // Clean URL
            window.history.replaceState({}, '', window.location.pathname);
            // Auto-capture the payment
            setCheckoutLoading(true);
            axios.post(`${API}/orders/capture`, {
                paypal_order_id: orderData.paypal_order_id,
                product_id: orderData.product_id,
                payer_id: payerId,
            }, { withCredentials: true }).then(r => {
                setCheckoutSuccess(r.data);
                setCheckoutProduct({ product_id: orderData.product_id, title: orderData.product_title, type: orderData.product_type, price: orderData.amount });
                setCheckoutData(orderData);
                // Scroll to shop section
                setTimeout(() => document.getElementById('shop')?.scrollIntoView({ behavior: 'smooth' }), 500);
            }).catch(e => {
                setError(e.response?.data?.detail || 'Payment capture failed. Please contact support.');
            }).finally(() => setCheckoutLoading(false));
        } catch { localStorage.removeItem('kys_pending_paypal'); }
    }, [user]);

    const statusLabel = (type) => type === 'ebook' ? 'E-Book / Digital Download' : 'Physical Book';

    const handleBuyNow = (product) => {
        if (!user) {
            setAuthGateProduct(product);
            setAuthTab('login');
            setAuthError('');
            setLoginForm({ email: '', password: '' });
            setRegisterForm({ full_name: '', email: '', password: '', confirm_password: '' });
            return;
        }
        // Show checkout modal first (let user enter coupon), don't auto-create order
        setCheckoutProduct(product);
        setCheckoutData(null);
        setCheckoutSuccess(null);
        setError('');
        setCouponCode('');
        setCouponResult(null);
    };

    const proceedToPayment = async (forceCardLogout = false) => {
        if (!checkoutProduct) return;
        setCheckoutLoading(true); setError('');
        try {
            const payload = { product_id: checkoutProduct.product_id, client_origin: window.location.origin };
            if (couponCode.trim()) payload.coupon_code = couponCode.trim();
            const r = await axios.post(`${API}/orders/create`, payload, { withCredentials: true });
            setCheckoutData(r.data);
            if (r.data.mode === 'live' && r.data.approval_url) {
                // Save order data before redirecting to PayPal
                localStorage.setItem('kys_pending_paypal', JSON.stringify({
                    paypal_order_id: r.data.paypal_order_id,
                    product_id: r.data.product_id,
                    product_title: r.data.product_title,
                    product_type: r.data.product_type,
                    amount: r.data.amount,
                }));
                if (forceCardLogout) {
                    // Send buyer through PayPal's signout endpoint, then back
                    // to the original approval URL untouched. Do NOT modify the
                    // approval URL — appending params or chaining nested
                    // returnUri redirects can drop the EC token (PayPal will
                    // show "No token passed").
                    const target = r.data.approval_url;
                    window.location.href = `https://www.paypal.com/signout?returnUri=${encodeURIComponent(target)}`;
                } else {
                    window.location.href = r.data.approval_url;
                }
                return;
            }
        } catch(e) {
            setError(e.response?.data?.detail || 'Failed to start checkout');
        } finally { setCheckoutLoading(false); }
    };

    const handleAuthLogin = async (e) => {
        e.preventDefault(); setAuthError(''); setAuthLoading(true);
        try {
            const r = await axios.post(`${API}/auth/login`, loginForm, { withCredentials: true });
            if (r.data.role === 'admin') {
                window.location.href = '/admin';
                return;
            }
            setUser(r.data);
            const pending = authGateProduct;
            setAuthGateProduct(null);
            // Show checkout modal (let user enter coupon first)
            setCheckoutProduct(pending);
            setCheckoutData(null); setCheckoutSuccess(null); setError(''); setCouponCode(''); setCouponResult(null);
        } catch(err) {
            setAuthError(err.response?.data?.detail || 'Login failed');
        } finally { setAuthLoading(false); }
    };

    const handleAuthRegister = async (e) => {
        e.preventDefault(); setAuthError(''); setAuthLoading(true);
        try {
            const r = await axios.post(`${API}/auth/register`, registerForm, { withCredentials: true });
            setUser(r.data);
            const pending = authGateProduct;
            setAuthGateProduct(null);
            setCheckoutProduct(pending);
            setCheckoutData(null); setCheckoutSuccess(null); setError(''); setCouponCode(''); setCouponResult(null);
        } catch(err) {
            setAuthError(err.response?.data?.detail || 'Registration failed');
        } finally { setAuthLoading(false); }
    };

    const handleConfirmPurchase = async () => {
        if (!checkoutData) return;
        setCheckoutLoading(true); setError('');
        try {
            const r = await axios.post(`${API}/orders/capture`, {
                paypal_order_id: checkoutData.paypal_order_id,
                product_id: checkoutData.product_id
            }, { withCredentials: true });
            setCheckoutSuccess(r.data);
            setCouponCode(''); setCouponResult(null);
        } catch(e) {
            setError(e.response?.data?.detail || 'Payment processing failed');
        } finally { setCheckoutLoading(false); }
    };

    const applyCoupon = async () => {
        if (!couponCode.trim() || !checkoutProduct) return;
        try {
            const r = await axios.post(`${API}/coupons/validate`, { code: couponCode, price: checkoutProduct.price });
            setCouponResult(r.data);
        } catch (e) {
            setCouponResult({ valid: false, error: e.response?.data?.detail || 'Invalid coupon' });
        }
    };

    const handleDownload = async (token, productTitle) => {
        try {
            const r = await axios.get(`${API}/downloads/file?token=${token}`, {
                withCredentials: true, responseType: 'blob'
            });
            const disposition = r.headers['content-disposition'] || '';
            const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
            const filename = filenameMatch ? filenameMatch[1] : `${productTitle || 'know-your-stuff'}.pdf`;
            const url = URL.createObjectURL(r.data);
            const a = document.createElement('a');
            a.href = url; a.download = filename;
            document.body.appendChild(a); a.click();
            document.body.removeChild(a); URL.revokeObjectURL(url);
        } catch(err) {
            alert(err.response?.data?.detail || 'Download failed. Please try again.');
        }
    };

    const inputCls = "w-full bg-white text-navy border border-gray-300 rounded-sm px-4 py-3 font-body text-base min-h-[44px] focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent";

    return (
        <section id="shop" className="bg-ivory py-20 md:py-28" data-testid="shop-section">
            <div className="max-w-6xl mx-auto px-6 md:px-12">
                <div className="text-center mb-12">
                    <h2 className="font-heading text-4xl md:text-5xl text-navy font-bold">Shop the Book</h2>
                    <div className="w-20 h-1 bg-gold mx-auto mt-4"></div>
                    <p className="font-body text-navy/60 mt-4 text-base">Browse freely — create an account only when you're ready to purchase.</p>
                </div>

                {loading && <p className="text-center text-navy/60 font-body text-lg">Loading products...</p>}
                {!loading && products.length === 0 && (
                    <p className="text-center text-navy/60 font-body text-lg py-10" data-testid="no-products-msg">No products are currently available. Please check back soon.</p>
                )}

                <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8" data-testid="products-grid">
                    {products.map(product => {
                        const truncatedDesc = product.description && product.description.length > 160
                            ? product.description.slice(0, 160) + '...'
                            : product.description;
                        const images = [
                            product.grid_url || (product.image_path ? `${API}/files/${product.image_path}` : BOOK_COVER),
                            ...(product.additional_urls || (product.additional_images || []).map(p => `${API}/files/${p}`))
                        ];
                        const productUrl = `${window.location.origin}/product/${product.product_id}`;
                        return (
                        <div key={product.product_id} className="bg-white rounded shadow-md overflow-hidden border border-gray-100 flex flex-col cursor-pointer hover:shadow-lg hover:border-gold/30 transition-all group" data-testid={`product-card-${product.product_id}`} onClick={() => setSelectedProduct(product)}>
                            <div className="relative bg-navy/5 aspect-[2/3] flex items-center justify-center" data-testid={`product-card-image-wrap-${product.product_id}`}>
                                <img src={images[0]} alt={product.title} className="w-full h-full object-contain p-2 sm:p-3 md:p-4 group-hover:scale-[1.03] transition-transform" loading="lazy" onError={(e)=>{e.target.src=BOOK_COVER;}}/>
                                {images.length > 1 && (
                                    <span className="absolute bottom-2 right-2 bg-navy/80 text-white text-xs font-body px-2 py-1 rounded" data-testid={`image-count-${product.product_id}`}>
                                        +{images.length - 1} photos
                                    </span>
                                )}
                            </div>
                            <div className="p-3 sm:p-4 md:p-6 flex flex-col flex-1">
                                <span className={`text-[10px] sm:text-xs font-body font-bold px-2 sm:px-3 py-1 rounded-sm w-fit mb-2 sm:mb-3 ${product.type === 'ebook' ? 'bg-gold text-navy' : 'bg-ivory text-navy border border-navy/20'}`} data-testid={`product-type-badge-${product.product_id}`}>
                                    {statusLabel(product.type)}
                                </span>
                                <h3 className="font-heading text-base sm:text-lg md:text-xl font-bold text-navy mb-1 sm:mb-2">{product.title}</h3>
                                {truncatedDesc && <p className="hidden sm:block font-body text-navy/70 text-sm md:text-base mb-4 flex-1">{truncatedDesc}</p>}
                                <div className="flex items-center justify-between mt-auto pt-3 sm:pt-4 border-t border-gray-100 gap-2 flex-wrap">
                                    <span className="font-heading text-lg sm:text-xl md:text-2xl font-bold text-gold" data-testid={`product-price-${product.product_id}`}>${parseFloat(product.price).toFixed(2)}</span>
                                    <div className="flex items-center gap-2 sm:gap-3">
                                        <div className="hidden sm:flex gap-1" onClick={e => e.stopPropagation()}>
                                            <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(productUrl)}`} target="_blank" rel="noopener noreferrer" className="text-navy/30 hover:text-blue-600 transition-colors" data-testid={`share-fb-${product.product_id}`} title="Share on Facebook">
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                                            </a>
                                            <a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(productUrl)}&text=${encodeURIComponent(product.title)}`} target="_blank" rel="noopener noreferrer" className="text-navy/30 hover:text-black transition-colors" data-testid={`share-x-${product.product_id}`} title="Share on X">
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                                            </a>
                                            <button onClick={() => {navigator.clipboard.writeText(productUrl);}} className="text-navy/30 hover:text-gold transition-colors" data-testid={`share-link-${product.product_id}`} title="Copy link">
                                                <Link2 size={16}/>
                                            </button>
                                        </div>
                                        <span className="font-body text-xs sm:text-sm text-gold font-semibold group-hover:underline whitespace-nowrap">View Details</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        );
                    })}
                </div>
            </div>

            {/* ── Product Detail Modal ── */}
            {selectedProduct && (
                <ProductDetailModal
                    product={selectedProduct}
                    onClose={() => setSelectedProduct(null)}
                    onBuyNow={(p) => handleBuyNow(p)}
                    onViewFullPage={() => { const pid = selectedProduct.product_id; setSelectedProduct(null); navigate(`/product/${pid}`); }}
                />
            )}

            {/* ── Auth Gate Modal (shown when Buy Now clicked while logged out) ── */}
            {authGateProduct && (
                <div className="fixed inset-0 bg-navy/80 backdrop-blur flex items-center justify-center z-50 p-4" data-testid="auth-gate-modal">
                    <div className="bg-white rounded max-w-md w-full p-8 relative max-h-[90vh] overflow-y-auto">
                        <button onClick={()=>setAuthGateProduct(null)} className="absolute top-4 right-4 text-navy/40 hover:text-navy" data-testid="auth-gate-close-btn"><X size={20}/></button>
                        <h3 className="font-heading text-2xl font-bold text-navy mb-1">Sign in to Purchase</h3>
                        <p className="font-body text-navy/60 text-sm mb-6">
                            Purchasing: <span className="font-semibold text-navy">{authGateProduct.title}</span> — <span className="text-gold font-bold">${parseFloat(authGateProduct.price).toFixed(2)}</span>
                        </p>

                        {/* Tabs */}
                        <div className="flex border-b border-gray-200 mb-6">
                            {[['login','Login'],['register','Create Account']].map(([t,l])=>(
                                <button key={t} onClick={()=>{setAuthTab(t);setAuthError('');}} className={`font-body font-semibold px-5 py-2 text-base border-b-2 transition-colors min-h-[44px] ${authTab===t?'border-gold text-gold':'border-transparent text-navy/60 hover:text-navy'}`} data-testid={`auth-gate-tab-${t}`}>{l}</button>
                            ))}
                        </div>

                        {authTab === 'login' ? (
                            <form onSubmit={handleAuthLogin} className="space-y-4" data-testid="auth-gate-login-form">
                                <div><label className="font-body text-navy/70 text-sm font-semibold block mb-1">Email</label>
                                    <input type="email" required value={loginForm.email} onChange={e=>setLoginForm({...loginForm,email:e.target.value})} className={inputCls} data-testid="auth-gate-email-input"/></div>
                                <div><label className="font-body text-navy/70 text-sm font-semibold block mb-1">Password</label>
                                    <input type="password" required value={loginForm.password} onChange={e=>setLoginForm({...loginForm,password:e.target.value})} className={inputCls} data-testid="auth-gate-password-input"/></div>
                                {authError && <p className="text-red-600 text-sm font-body bg-red-50 border border-red-200 rounded p-3" data-testid="auth-gate-error">{authError}</p>}
                                <button type="submit" disabled={authLoading} className="w-full bg-gold hover:bg-gold-hover disabled:opacity-50 text-navy font-bold py-3 rounded-sm font-body text-lg transition-colors min-h-[44px]" data-testid="auth-gate-login-submit-btn">
                                    {authLoading ? 'Signing in...' : 'Login & Continue to Purchase'}
                                </button>
                                {/* Google */}
                                <div className="border-t border-gray-200 pt-4">
                                    <button type="button" onClick={()=>{
                                        sessionStorage.setItem('authGateProduct', JSON.stringify(authGateProduct));
                                        const redirectUrl = window.location.origin + '/';
                                        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                                    }} className="w-full border border-gray-300 text-navy bg-white hover:bg-gray-50 font-body font-semibold py-3 rounded-sm flex items-center justify-center gap-3 transition-colors min-h-[44px]" data-testid="auth-gate-google-btn">
                                        <svg width="20" height="20" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                                        Continue with Google
                                    </button>
                                </div>
                            </form>
                        ) : (
                            <form onSubmit={handleAuthRegister} className="space-y-4" data-testid="auth-gate-register-form">
                                <div><label className="font-body text-navy/70 text-sm font-semibold block mb-1">Full Name</label>
                                    <input type="text" required value={registerForm.full_name} onChange={e=>setRegisterForm({...registerForm,full_name:e.target.value})} className={inputCls} data-testid="auth-gate-name-input"/></div>
                                <div><label className="font-body text-navy/70 text-sm font-semibold block mb-1">Email</label>
                                    <input type="email" required value={registerForm.email} onChange={e=>setRegisterForm({...registerForm,email:e.target.value})} className={inputCls} data-testid="auth-gate-reg-email-input"/></div>
                                <div><label className="font-body text-navy/70 text-sm font-semibold block mb-1">Password</label>
                                    <input type="password" required value={registerForm.password} onChange={e=>setRegisterForm({...registerForm,password:e.target.value})} className={inputCls} placeholder="Minimum 8 characters" data-testid="auth-gate-reg-password-input"/></div>
                                <div><label className="font-body text-navy/70 text-sm font-semibold block mb-1">Confirm Password</label>
                                    <input type="password" required value={registerForm.confirm_password} onChange={e=>setRegisterForm({...registerForm,confirm_password:e.target.value})} className={inputCls} data-testid="auth-gate-reg-confirm-input"/></div>
                                {authError && <p className="text-red-600 text-sm font-body bg-red-50 border border-red-200 rounded p-3" data-testid="auth-gate-error">{authError}</p>}
                                <button type="submit" disabled={authLoading} className="w-full bg-navy hover:bg-navy/80 disabled:opacity-50 text-white font-bold py-3 rounded-sm font-body text-lg transition-colors min-h-[44px]" data-testid="auth-gate-register-submit-btn">
                                    {authLoading ? 'Creating account...' : 'Create Account & Purchase'}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            )}

            {/* ── Checkout Modal ── */}
            {checkoutProduct && (
                <div className="fixed inset-0 bg-navy/80 backdrop-blur flex items-center justify-center z-50 p-4" data-testid="checkout-modal">
                    <div className="bg-white rounded max-w-md w-full p-8 relative">
                        <button onClick={()=>{setCheckoutProduct(null);setError('');}} className="absolute top-4 right-4 text-navy/40 hover:text-navy" data-testid="checkout-close-btn"><X size={20}/></button>
                        {!checkoutSuccess ? (
                            <>
                                <h3 className="font-heading text-2xl font-bold text-navy mb-2">Complete Your Order</h3>
                                {checkoutData?.mode === 'demo' && (
                                    <p className="font-body text-navy/60 text-sm mb-6 bg-ivory/50 border border-gold/30 rounded p-3">
                                        Demo checkout — PayPal integration will be activated when credentials are configured in Admin Settings.
                                    </p>
                                )}
                                <div className="space-y-3 font-body text-navy mb-4">
                                    <div className="flex justify-between"><span className="text-navy/70">Product:</span><span className="font-semibold">{checkoutProduct.title}</span></div>
                                    <div className="flex justify-between"><span className="text-navy/70">Type:</span><span>{statusLabel(checkoutProduct.type)}</span></div>
                                    {checkoutData?.coupon_applied && (
                                        <div className="flex justify-between text-green-700"><span>Coupon ({checkoutData.coupon_applied}):</span><span>-${(checkoutData.original_price - checkoutData.amount).toFixed(2)}</span></div>
                                    )}
                                    <div className="flex justify-between border-t pt-3"><span className="font-bold">Total:</span><span className="font-bold text-gold text-xl">${checkoutData ? parseFloat(checkoutData.amount).toFixed(2) : parseFloat(checkoutProduct.price).toFixed(2)}</span></div>
                                </div>
                                {/* Coupon Input — shown before order is created */}
                                {!checkoutData && (
                                    <div className="flex gap-2 mb-4" data-testid="coupon-input-section">
                                        <input type="text" value={couponCode} onChange={e=>setCouponCode(e.target.value.toUpperCase())} className="flex-1 bg-white text-navy border border-gray-300 rounded-sm px-3 py-2 font-body text-sm min-h-[40px]" placeholder="Coupon code (optional)" data-testid="checkout-coupon-input"/>
                                        <button type="button" onClick={applyCoupon} className="bg-navy text-white font-body font-bold px-4 py-2 rounded-sm text-sm min-h-[40px]" data-testid="apply-coupon-btn">Apply</button>
                                    </div>
                                )}
                                {couponResult && !couponResult.valid && <p className="text-red-600 text-xs font-body mb-2">{couponResult.error}</p>}
                                {couponResult && couponResult.valid && <p className="text-green-700 text-xs font-body mb-2">Coupon applied: {couponResult.discount_type === 'percentage' ? `${couponResult.discount_value}% off` : `$${couponResult.discount_value} off`} (-${couponResult.discount_amount?.toFixed(2)})</p>}
                                {error && <p className="text-red-600 text-sm font-body mb-4 bg-red-50 border border-red-200 rounded p-3">{error}</p>}
                                {checkoutData ? (
                                    <button onClick={handleConfirmPurchase} disabled={checkoutLoading} className="w-full bg-gold hover:bg-gold-hover disabled:opacity-50 text-navy font-bold py-4 rounded-sm font-body text-lg transition-colors min-h-[56px]" data-testid="confirm-purchase-btn">
                                        {checkoutLoading ? 'Processing...' : checkoutData.mode === 'demo' ? 'Complete Purchase (Demo)' : 'Complete Purchase'}
                                    </button>
                                ) : (
                                    <>
                                        <button onClick={() => proceedToPayment(false)} disabled={checkoutLoading} className="w-full bg-gold hover:bg-gold-hover disabled:opacity-50 text-navy font-bold py-4 rounded-sm font-body text-lg transition-colors min-h-[56px]" data-testid="proceed-to-payment-btn">
                                            {checkoutLoading ? 'Preparing checkout...' : 'Proceed to Payment'}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => proceedToPayment(true)}
                                            disabled={checkoutLoading}
                                            className="w-full mt-2 bg-white hover:bg-ivory border-2 border-navy text-navy font-bold py-3 rounded-sm font-body text-sm transition-colors min-h-[44px] disabled:opacity-50"
                                            data-testid="pay-with-card-btn"
                                            title="Sign out of any saved PayPal session and pay as a guest with a credit or debit card"
                                        >
                                            Pay with Credit / Debit Card (No PayPal account)
                                        </button>
                                        <p className="font-body text-xs text-navy/50 text-center mt-2">
                                            Already signed into PayPal? The second option signs you out of PayPal first so you can pay as a guest with a new card. <strong>Tip:</strong> if PayPal still shows saved cards you don't want to use, open the site in a private/incognito window for a fully clean checkout.
                                        </p>
                                    </>
                                )}
                            </>
                        ) : (
                            <div className="text-center space-y-4" data-testid="checkout-success">
                                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                                    <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/></svg>
                                </div>
                                <h3 className="font-heading text-2xl font-bold text-navy">Order Confirmed!</h3>
                                <p className="font-body text-navy/70">
                                    {checkoutSuccess.product_type === 'ebook' ? 'Your e-book is ready! Download it now or from your My Account section. A confirmation email has been sent.' : 'Your physical book order is being processed and will ship soon. A confirmation email has been sent.'}
                                </p>
                                {checkoutSuccess.product_type === 'ebook' && checkoutSuccess.download_token && (
                                    <button onClick={()=>handleDownload(checkoutSuccess.download_token, checkoutProduct?.title)} className="w-full bg-navy hover:bg-navy/80 text-white font-bold px-8 py-3 rounded-sm font-body transition-colors min-h-[44px] flex items-center justify-center gap-2" data-testid="immediate-download-btn">
                                        <Download size={18}/> Download E-Book Now
                                    </button>
                                )}
                                <button onClick={()=>{setCheckoutProduct(null);document.getElementById('account')?.scrollIntoView({behavior:'smooth'});}} className="bg-gold text-navy font-bold px-8 py-3 rounded-sm font-body transition-colors min-h-[44px]" data-testid="go-to-account-btn">
                                    {checkoutSuccess.product_type === 'ebook' ? 'Go to My Downloads' : 'View My Orders'}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </section>
    );
}

// ─── CUSTOMER ORDER CARD ────────────────────────────────────────
// Replaces the old table row. Shows product, status, shipping address with
// inline edit (only when not yet shipped), tracking info once shipped.
function CustomerOrderCard({ order, onUpdate }) {
    const statusLabel = s => ({ delivered_digital: 'Delivered (Digital)', shipped: 'Shipped', delivered: 'Delivered', cancelled: 'Cancelled', pending_fulfillment: 'Processing' }[s] || 'Processing');
    const statusBadge = s => ({
        delivered_digital: 'bg-green-100 text-green-800',
        shipped: 'bg-blue-100 text-blue-800',
        delivered: 'bg-green-100 text-green-700',
        cancelled: 'bg-red-100 text-red-700',
    }[s] || 'bg-yellow-100 text-yellow-800');

    const isPhysical = order.product_snapshot?.type === 'physical';
    const isAudiobook = order.product_snapshot?.type === 'audiobook';
    const isShipped = !!order.shippo_label_url || ['shipped', 'delivered'].includes(order.status);
    const canEdit = isPhysical && !isShipped && order.status !== 'cancelled';

    const ship = order.shipping_address || {};
    const [editing, setEditing] = useState(false);
    const [addr, setAddr] = useState({
        recipient_name: ship.recipient_name || '',
        line1: ship.line1 || '',
        line2: ship.line2 || '',
        city: ship.city || '',
        state: ship.state || '',
        postal_code: ship.postal_code || '',
        phone: ship.phone || '',
    });
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState('');
    const [saved, setSaved] = useState(false);

    const save = async () => {
        setSaving(true); setErr('');
        try {
            await axios.patch(`${API}/account/orders/${order.order_id}/shipping-address`, addr, { withCredentials: true });
            setEditing(false);
            setSaved(true);
            setTimeout(() => setSaved(false), 2500);
            onUpdate && onUpdate();
        } catch (ex) {
            setErr(ex.response?.data?.detail || 'Could not save address.');
        } finally {
            setSaving(false);
        }
    };

    const inputCls = "w-full bg-white text-navy border border-gray-300 rounded-sm px-3 py-2 font-body text-sm focus:outline-none focus:ring-2 focus:ring-gold";

    return (
        <div className="bg-ivory rounded-lg p-5 border border-gray-200" data-testid={`customer-order-${order.order_id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                <div>
                    <div className="font-heading text-lg text-navy font-bold">{order.product_snapshot?.title}</div>
                    <div className="font-body text-navy/60 text-sm">
                        {new Date(order.created_at).toLocaleDateString()} · {isPhysical ? 'Physical Book' : isAudiobook ? 'Audiobook' : 'E-Book'} · ${parseFloat(order.amount).toFixed(2)}
                    </div>
                </div>
                <span className={`px-3 py-1 rounded-sm text-xs font-bold ${statusBadge(order.status)}`} data-testid={`customer-order-status-${order.order_id}`}>
                    {statusLabel(order.status)}
                </span>
            </div>

            {isShipped && order.tracking_number && (
                <div className="bg-blue-50 border border-blue-200 rounded p-4 mb-3" data-testid={`customer-tracking-banner-${order.order_id}`}>
                    <div className="font-body text-blue-900 font-bold mb-1">📦 Your order has shipped!</div>
                    <div className="font-body text-sm text-navy/80">
                        Tracking: <span className="font-mono font-bold" data-testid={`customer-tracking-number-${order.order_id}`}>{order.tracking_number}</span>
                    </div>
                    <a href={`https://tools.usps.com/go/TrackConfirmAction?tLabels=${order.tracking_number}`} target="_blank" rel="noopener noreferrer" className="inline-block mt-2 text-sm bg-navy text-white font-bold px-4 py-2 rounded hover:bg-navy/90" data-testid={`customer-track-btn-${order.order_id}`}>
                        Track Package on USPS
                    </a>
                </div>
            )}

            {isAudiobook && order.status === 'delivered_digital' && (
                <div className="bg-navy rounded p-4 flex items-center justify-between gap-4" data-testid={`audiobook-order-cta-${order.order_id}`}>
                    <div className="min-w-0">
                        <div className="font-body text-gold uppercase tracking-wider text-[11px] font-bold mb-1">Audiobook Ready</div>
                        <div className="font-body text-white text-sm">Unlimited streaming · up to 100 downloads.</div>
                    </div>
                    <a href={`/audiobook?product_id=${order.product_id}`} className="bg-gold hover:bg-gold-hover text-navy font-bold px-4 py-2 rounded font-body text-sm whitespace-nowrap min-h-[40px] flex items-center" data-testid={`open-audiobook-btn-${order.order_id}`}>
                        Open Player
                    </a>
                </div>
            )}

            {isPhysical && (
                <div className="bg-white rounded p-4 border border-gray-100">
                    <div className="flex items-center justify-between mb-2">
                        <div className="font-body text-navy/60 text-xs uppercase tracking-wider">Shipping Address</div>
                        {canEdit && !editing && (
                            <button type="button" onClick={() => setEditing(true)} className="text-xs text-gold hover:underline font-semibold" data-testid={`customer-edit-address-btn-${order.order_id}`}>
                                {ship.line1 ? 'Edit address' : 'Add address'}
                            </button>
                        )}
                        {!canEdit && isShipped && (
                            <span className="text-xs text-navy/40 font-semibold" data-testid={`customer-address-locked-${order.order_id}`}>Locked — already shipped</span>
                        )}
                    </div>
                    {!editing ? (
                        ship.line1 ? (
                            <div className="font-body text-sm text-navy space-y-0.5">
                                <div className="font-semibold">{ship.recipient_name}</div>
                                <div>{ship.line1}</div>
                                {ship.line2 && <div>{ship.line2}</div>}
                                <div>{[ship.city, ship.state, ship.postal_code].filter(Boolean).join(', ')}</div>
                                {saved && <div className="text-green-700 text-xs font-bold mt-1">Saved ✓</div>}
                            </div>
                        ) : (
                            <div className="font-body text-sm text-yellow-800 bg-yellow-50 border border-yellow-200 rounded p-3" data-testid={`customer-no-address-${order.order_id}`}>
                                We don't have a shipping address for this order yet. Please add one so we can ship your book.
                            </div>
                        )
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid={`customer-edit-address-form-${order.order_id}`}>
                            <input type="text" placeholder="Full Name *" value={addr.recipient_name} onChange={e=>setAddr({...addr,recipient_name:e.target.value})} className={inputCls + ' sm:col-span-2'} data-testid={`customer-recipient-input-${order.order_id}`}/>
                            <input type="text" placeholder="Street Address Line 1 *" value={addr.line1} onChange={e=>setAddr({...addr,line1:e.target.value})} className={inputCls + ' sm:col-span-2'} data-testid={`customer-line1-input-${order.order_id}`}/>
                            <input type="text" placeholder="Apt / Suite (optional)" value={addr.line2} onChange={e=>setAddr({...addr,line2:e.target.value})} className={inputCls + ' sm:col-span-2'} data-testid={`customer-line2-input-${order.order_id}`}/>
                            <input type="text" placeholder="City *" value={addr.city} onChange={e=>setAddr({...addr,city:e.target.value})} className={inputCls} data-testid={`customer-city-input-${order.order_id}`}/>
                            <div className="grid grid-cols-2 gap-2">
                                <input type="text" maxLength={2} placeholder="State *" value={addr.state} onChange={e=>setAddr({...addr,state:e.target.value.toUpperCase()})} className={inputCls} data-testid={`customer-state-input-${order.order_id}`}/>
                                <input type="text" placeholder="ZIP *" value={addr.postal_code} onChange={e=>setAddr({...addr,postal_code:e.target.value})} className={inputCls} data-testid={`customer-zip-input-${order.order_id}`}/>
                            </div>
                            <input type="tel" placeholder="Phone (optional)" value={addr.phone} onChange={e=>setAddr({...addr,phone:e.target.value})} className={inputCls + ' sm:col-span-2'} data-testid={`customer-phone-input-${order.order_id}`}/>
                            {err && <p className="sm:col-span-2 text-red-600 text-xs font-semibold">{err}</p>}
                            <div className="sm:col-span-2 flex gap-2 justify-end pt-1">
                                <button type="button" onClick={()=>{ setEditing(false); setErr(''); }} className="text-xs text-navy/60 px-3 py-2 hover:text-navy" data-testid={`customer-cancel-address-${order.order_id}`}>Cancel</button>
                                <button type="button" onClick={save} disabled={saving} className="text-xs bg-gold text-navy font-bold px-4 py-2 rounded hover:bg-gold/90 disabled:opacity-60" data-testid={`customer-save-address-${order.order_id}`}>
                                    {saving ? 'Saving…' : 'Save Address'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}


// ─── MY ACCOUNT ──────────────────────────────────────────────────
function MyAccountSection({ pendingProduct }) {
    const { user, setUser } = useAuth();
    const [activeTab, setActiveTab] = useState('orders');
    const [loginForm, setLoginForm] = useState({ email: '', password: '' });
    const [registerForm, setRegisterForm] = useState({ full_name: '', email: '', password: '', confirm_password: '', phone_number: '' });
    const [forgotMode, setForgotMode] = useState(false);
    const [forgotEmail, setForgotEmail] = useState('');
    const [forgotMsg, setForgotMsg] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [orders, setOrders] = useState([]);
    const [library, setLibrary] = useState([]);
    const [profileForm, setProfileForm] = useState({ full_name: '', email: '', phone_number: '', new_password: '', confirm_new_password: '' });
    const [loadingData, setLoadingData] = useState(false);

    useEffect(() => {
        if (user) {
            setProfileForm({ full_name: user.full_name, email: user.email, phone_number: user.phone_number || '', new_password: '', confirm_new_password: '' });
            loadAccountData();
        }
    }, [user]);

    const loadAccountData = async () => {
        setLoadingData(true);
        try {
            const [ordersR, libraryR] = await Promise.all([
                axios.get(`${API}/account/orders`, { withCredentials: true }),
                axios.get(`${API}/account/library`, { withCredentials: true })
            ]);
            setOrders(ordersR.data);
            setLibrary(libraryR.data);
        } catch {}
        setLoadingData(false);
    };

    const handleLogin = async (e) => {
        e.preventDefault(); setError(''); setSuccess('');
        try {
            const r = await axios.post(`${API}/auth/login`, loginForm, { withCredentials: true });
            if (r.data.role === 'admin') {
                window.location.href = '/admin';
                return;
            }
            setUser(r.data);
            setLoginForm({ email: '', password: '' });
        } catch(err) { setError(err.response?.data?.detail || 'Login failed'); }
    };

    const handleRegister = async (e) => {
        e.preventDefault(); setError(''); setSuccess('');
        try {
            const r = await axios.post(`${API}/auth/register`, registerForm, { withCredentials: true });
            setUser(r.data);
        } catch(err) { setError(err.response?.data?.detail || 'Registration failed'); }
    };

    const handleForgotPassword = async (e) => {
        e.preventDefault();
        try {
            const r = await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
            setForgotMsg(r.data.message);
        } catch { setForgotMsg('If an account with that email exists, a reset link has been sent.'); }
    };

    const handleProfileUpdate = async (e) => {
        e.preventDefault(); setError(''); setSuccess('');
        try {
            const r = await axios.put(`${API}/account/profile`, profileForm, { withCredentials: true });
            setUser(prev => ({ ...prev, full_name: r.data.full_name, email: r.data.email }));
            setSuccess('Profile updated successfully.');
        } catch(err) { setError(err.response?.data?.detail || 'Update failed'); }
    };

    const handleDownload = async (token, productTitle) => {
        try {
            const r = await axios.get(`${API}/ebook/download?token=${token}`, {
                withCredentials: true, responseType: 'blob'
            });
            const disposition = r.headers['content-disposition'] || '';
            const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
            let filename = filenameMatch ? filenameMatch[1] : 'KnowYourStuff_AliceMWhite.pdf';
            const url = URL.createObjectURL(r.data);
            const a = document.createElement('a');
            a.href = url; a.download = filename;
            document.body.appendChild(a); a.click();
            document.body.removeChild(a); URL.revokeObjectURL(url);
            await loadAccountData();
        } catch(err) {
            alert(err.response?.data?.detail || 'Download failed. Please try again.');
        }
    };

    const handleReadOnline = (token) => {
        window.location.href = `/read?token=${token}`;
    };

    const statusLabel = (s) => ({ pending_fulfillment:'Processing', shipped:'Shipped', delivered:'Delivered', cancelled:'Cancelled', delivered_digital:'Available for Download' }[s] || s);

    const inputCls = "w-full bg-white text-navy border border-gray-300 rounded-sm px-4 py-3 font-body text-base min-h-[44px] focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent";
    const labelCls = "font-body text-navy/70 text-sm font-semibold block mb-1";

    return (
        <section id="account" className="bg-white py-20 md:py-28" data-testid="account-section">
            <div className="max-w-5xl mx-auto px-6 md:px-12">
                <div className="text-center mb-12">
                    <h2 className="font-heading text-4xl md:text-5xl text-navy font-bold">My Account</h2>
                    <p className="font-body text-navy/60 mt-3 text-base">Read the E-Book, manage your orders, and access your library.</p>
                    <div className="w-20 h-1 bg-gold mx-auto mt-4"></div>
                </div>

                {!user ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                        {/* LOGIN */}
                        <div className="bg-ivory rounded p-8">
                            <h3 className="font-heading text-2xl text-navy font-bold mb-6">Login</h3>
                            {!forgotMode ? (
                                <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
                                    <div><label className={labelCls}>Email</label><input type="email" required value={loginForm.email} onChange={e=>setLoginForm({...loginForm,email:e.target.value})} className={inputCls} data-testid="login-email-input"/></div>
                                    <div><label className={labelCls}>Password</label><input type="password" required value={loginForm.password} onChange={e=>setLoginForm({...loginForm,password:e.target.value})} className={inputCls} data-testid="login-password-input"/></div>
                                    {error && <p className="text-red-600 text-sm font-body bg-red-50 border border-red-200 rounded p-3" data-testid="login-error">{error}</p>}
                                    <button type="submit" className="w-full bg-gold hover:bg-gold-hover text-navy font-bold py-3 rounded-sm font-body text-lg transition-colors min-h-[44px]" data-testid="login-submit-btn">Login</button>
                                    <button type="button" onClick={()=>{setForgotMode(true);setError('');}} className="font-body text-gold hover:underline text-sm" data-testid="forgot-password-btn">Forgot Password?</button>
                                    {/* Google Login */}
                                    <div className="border-t border-gray-200 pt-4">
                                        <button type="button" onClick={()=>{
                                            // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                                            const redirectUrl = window.location.origin + '/';
                                            window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                                        }} className="w-full border border-gray-300 text-navy bg-white hover:bg-gray-50 font-body font-semibold py-3 rounded-sm flex items-center justify-center gap-3 transition-colors min-h-[44px]" data-testid="google-login-btn">
                                            <svg width="20" height="20" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                                            Continue with Google
                                        </button>
                                    </div>
                                </form>
                            ) : (
                                <div className="space-y-4" data-testid="forgot-password-form">
                                    <p className="font-body text-navy/70 text-base">Enter your email address and we'll send you a reset link.</p>
                                    {forgotMsg ? (
                                        <p className="text-green-700 bg-green-50 border border-green-200 rounded p-3 font-body text-sm">{forgotMsg}</p>
                                    ) : (
                                        <form onSubmit={handleForgotPassword} className="space-y-4">
                                            <input type="email" required value={forgotEmail} onChange={e=>setForgotEmail(e.target.value)} placeholder="Email address" className={inputCls} data-testid="forgot-email-input"/>
                                            <button type="submit" className="w-full bg-gold hover:bg-gold-hover text-navy font-bold py-3 rounded-sm font-body transition-colors min-h-[44px]" data-testid="send-reset-btn">Send Reset Link</button>
                                        </form>
                                    )}
                                    <button onClick={()=>{setForgotMode(false);setForgotMsg('');}} className="font-body text-gold hover:underline text-sm">Back to Login</button>
                                </div>
                            )}
                        </div>
                        {/* REGISTER */}
                        <div className="bg-ivory rounded p-8">
                            <h3 className="font-heading text-2xl text-navy font-bold mb-6">Create Account</h3>
                            <form onSubmit={handleRegister} className="space-y-4" data-testid="register-form">
                                <div><label className={labelCls}>Full Name</label><input type="text" required value={registerForm.full_name} onChange={e=>setRegisterForm({...registerForm,full_name:e.target.value})} className={inputCls} data-testid="register-name-input"/></div>
                                <div><label className={labelCls}>Email</label><input type="email" required value={registerForm.email} onChange={e=>setRegisterForm({...registerForm,email:e.target.value})} className={inputCls} data-testid="register-email-input"/></div>
                                <div><label className={labelCls}>Password</label><input type="password" required value={registerForm.password} onChange={e=>setRegisterForm({...registerForm,password:e.target.value})} className={inputCls} placeholder="Minimum 8 characters" data-testid="register-password-input"/></div>
                                <div><label className={labelCls}>Confirm Password</label><input type="password" required value={registerForm.confirm_password} onChange={e=>setRegisterForm({...registerForm,confirm_password:e.target.value})} className={inputCls} data-testid="register-confirm-password-input"/></div>
                                {error && <p className="text-red-600 text-sm font-body bg-red-50 border border-red-200 rounded p-3" data-testid="register-error">{error}</p>}
                                <button type="submit" className="w-full bg-navy hover:bg-navy/80 text-white font-bold py-3 rounded-sm font-body text-lg transition-colors min-h-[44px]" data-testid="register-submit-btn">Create Account</button>
                            </form>
                        </div>
                    </div>
                ) : (
                    <div data-testid="account-dashboard">
                        <div className="mb-8 flex items-center gap-4">
                            <div className="w-12 h-12 bg-gold rounded-full flex items-center justify-center">
                                <User size={24} className="text-navy"/>
                            </div>
                            <div>
                                <h3 className="font-heading text-2xl text-navy font-semibold">Welcome back, {user.full_name?.split(' ')[0]}!</h3>
                                <p className="font-body text-navy/60">{user.email}</p>
                            </div>
                        </div>

                        {/* Admin Panel card — shown FIRST for admin users */}
                        {user.role === 'admin' && (
                            <div className="bg-navy rounded-lg p-8 border border-gold/30 flex flex-col sm:flex-row items-center gap-6 mb-8" data-testid="admin-redirect-card">
                                <div className="w-14 h-14 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0">
                                    <LayoutDashboard size={28} className="text-gold"/>
                                </div>
                                <div className="flex-1 text-center sm:text-left">
                                    <h3 className="font-heading text-xl text-white font-bold mb-1">Admin Dashboard</h3>
                                    <p className="font-body text-white/60 text-sm">Manage products, orders, customers & settings.</p>
                                </div>
                                <a href="/admin" className="inline-block bg-gold hover:bg-gold-hover text-navy font-bold px-8 py-3 rounded-sm font-body text-base transition-colors min-h-[44px]" data-testid="go-to-admin-btn">
                                    Go to Admin Panel
                                </a>
                            </div>
                        )}

                        {/* Consumer tabs — shown for ALL logged-in users including admins */}
                        <div className="border-b border-gray-200 mb-8">
                            <div className="flex gap-0">
                                {[['orders','My Orders'],['library','My Library'],['profile','My Profile']].map(([t,l])=>(
                                    <button key={t} onClick={()=>setActiveTab(t)} className={`font-body font-semibold px-6 py-3 text-base border-b-2 transition-colors min-h-[44px] ${activeTab===t?'border-gold text-gold':'border-transparent text-navy/60 hover:text-navy'}`} data-testid={`tab-${t}-btn`}>{l}</button>
                                ))}
                            </div>
                        </div>

                        {activeTab==='orders' && (
                            <div data-testid="orders-tab">
                                {loadingData ? <p className="font-body text-navy/60">Loading...</p> :
                                orders.length === 0 ? <p className="font-body text-navy/60 text-lg py-8 text-center" data-testid="no-orders-msg">You haven't placed any orders yet.</p> : (
                                <div className="space-y-4" data-testid="orders-list">
                                    {orders.map(o => (
                                        <CustomerOrderCard key={o.order_id} order={o} onUpdate={() => {
                                            // Reload orders after address update
                                            axios.get(`${API}/account/orders`, { withCredentials: true })
                                                .then(r => setOrders(r.data))
                                                .catch(()=>{});
                                        }}/>
                                    ))}
                                </div>
                                )}
                            </div>
                        )}

                        {activeTab==='library' && (
                            <div data-testid="library-tab">
                                {loadingData ? <p className="font-body text-navy/60">Loading...</p> :
                                library.length === 0 ? <p className="font-body text-navy/60 text-lg py-8 text-center" data-testid="no-library-msg">No e-books in your library yet. Purchase an e-book to get started.</p> : (
                                <div className="space-y-6">
                                    {library.map(item => {
                                        const imgSrc = item.product_image ? (item.product_image.startsWith('http') ? item.product_image : `${process.env.REACT_APP_BACKEND_URL}/api/files/${item.product_image}`) : '';
                                        return (
                                            <div key={item.order_id} className="bg-ivory rounded-lg p-6 flex flex-col md:flex-row gap-6" data-testid={`library-item-${item.order_id}`}>
                                                {imgSrc && <img src={imgSrc} alt={item.product_title} className="w-24 h-32 object-cover rounded shadow-md flex-shrink-0"/>}
                                                <div className="flex-1 space-y-2">
                                                    <h4 className="font-heading text-xl text-navy font-bold">{item.product_title}</h4>
                                                    <p className="font-body text-navy/50 text-sm">by Alice M. White, B.S., MBA</p>
                                                    <p className="font-body text-navy/60 text-sm">Purchased: {new Date(item.purchase_date).toLocaleDateString()}</p>
                                                    <p className="font-body text-navy/60 text-sm">Access expires: <span className="font-bold text-navy">{new Date(item.expires_at).getFullYear() > 2099 ? 'Never' : new Date(item.expires_at).toLocaleDateString()}</span></p>
                                                    {item.is_expired ? (
                                                        <span className="inline-block font-body text-sm text-red-600 font-semibold bg-red-50 border border-red-200 px-4 py-2 rounded-sm mt-2" data-testid="library-expired">Access Expired</span>
                                                    ) : (
                                                        <div className="flex flex-wrap gap-3 mt-3">
                                                            {item.can_read && item.has_reader && (
                                                                <button onClick={() => handleReadOnline(item.download_token)} className="bg-gold hover:bg-gold-hover text-navy font-bold px-6 py-3 rounded-sm font-body flex items-center gap-2 transition-colors min-h-[44px]" data-testid={`read-online-btn-${item.order_id}`}>
                                                                    <BookOpen size={16}/> READ ONLINE
                                                                </button>
                                                            )}
                                                            {item.can_download && (
                                                                <button onClick={() => handleDownload(item.download_token, item.product_title)} className="bg-navy hover:bg-navy/90 text-gold font-bold px-6 py-3 rounded-sm font-body flex items-center gap-2 transition-colors min-h-[44px]" data-testid={`download-pdf-btn-${item.order_id}`}>
                                                                    <Download size={16}/> DOWNLOAD PDF
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}
                                                    <p className="font-body text-navy/40 text-xs mt-2">Unlimited reading online and PDF downloads.</p>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                )}
                            </div>
                        )}

                        {activeTab==='profile' && (
                            <div data-testid="profile-tab">
                                <form onSubmit={handleProfileUpdate} className="max-w-lg space-y-5">
                                    <div><label className={labelCls}>Full Name</label><input type="text" required value={profileForm.full_name} onChange={e=>setProfileForm({...profileForm,full_name:e.target.value})} className={inputCls} data-testid="profile-name-input"/></div>
                                    <div><label className={labelCls}>Email</label><input type="email" required value={profileForm.email} onChange={e=>setProfileForm({...profileForm,email:e.target.value})} className={inputCls} data-testid="profile-email-input"/></div>
                                    <div><label className={labelCls}>Phone Number <span className="text-navy/40 font-normal">(optional — for order notifications)</span></label><input type="tel" value={profileForm.phone_number} onChange={e=>setProfileForm({...profileForm,phone_number:e.target.value})} className={inputCls} placeholder="(555) 123-4567" data-testid="profile-phone-input"/></div>
                                    <div className="border-t border-gray-200 pt-5">
                                        <p className="font-body text-navy/60 text-sm mb-4">Leave password fields blank to keep current password.</p>
                                        <div className="space-y-4">
                                            <div><label className={labelCls}>New Password</label><input type="password" value={profileForm.new_password} onChange={e=>setProfileForm({...profileForm,new_password:e.target.value})} className={inputCls} placeholder="Optional — minimum 8 characters" data-testid="profile-new-password-input"/></div>
                                            <div><label className={labelCls}>Confirm New Password</label><input type="password" value={profileForm.confirm_new_password} onChange={e=>setProfileForm({...profileForm,confirm_new_password:e.target.value})} className={inputCls} data-testid="profile-confirm-password-input"/></div>
                                        </div>
                                    </div>
                                    {error && <p className="text-red-600 text-sm font-body bg-red-50 border border-red-200 rounded p-3" data-testid="profile-error">{error}</p>}
                                    {success && <p className="text-green-700 text-sm font-body bg-green-50 border border-green-200 rounded p-3" data-testid="profile-success">{success}</p>}
                                    <button type="submit" className="bg-gold hover:bg-gold-hover text-navy font-bold px-8 py-3 rounded-sm font-body transition-colors min-h-[44px]" data-testid="profile-save-btn">Save Changes</button>
                                </form>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </section>
    );
}

// ─── CONTACT ─────────────────────────────────────────────────────
function ContactSection() {
    const [form, setForm] = useState({ name: '', email: '', message: '' });
    const [sending, setSending] = useState(false);
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSending(true); setError(''); setSuccess('');
        try {
            const r = await axios.post(`${API}/contact`, form);
            setSuccess(r.data.message);
            setForm({ name: '', email: '', message: '' });
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to send message. Please try again.');
        } finally { setSending(false); }
    };

    const inputCls = "w-full bg-white/10 text-white border border-gold/30 rounded-sm px-4 py-3 font-body text-base min-h-[44px] focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent placeholder-white/40";

    return (
        <section id="contact" className="bg-navy py-20 md:py-28" data-testid="contact-section">
            <div className="max-w-4xl mx-auto px-6 md:px-12">
                <div className="text-center mb-12">
                    <h2 className="font-heading text-4xl md:text-5xl text-gold font-bold">Get in Touch</h2>
                    <div className="w-20 h-1 bg-gold mx-auto mt-4"></div>
                    <p className="font-body text-white/60 mt-4 text-base">Have a question about the book, a speaking engagement, or want to connect? Send a message below or email directly at</p>
                    <a href="mailto:Knowyourstuff53@gmail.com" className="font-body text-gold hover:text-gold-hover underline text-base transition-colors" data-testid="contact-email-link">Knowyourstuff53@gmail.com</a>
                </div>
                <div className="max-w-lg mx-auto">
                    <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                        <div>
                            <label className="font-body text-white/70 text-sm font-semibold block mb-1">Your Name</label>
                            <input type="text" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className={inputCls} placeholder="Full name" data-testid="contact-name-input" />
                        </div>
                        <div>
                            <label className="font-body text-white/70 text-sm font-semibold block mb-1">Email Address</label>
                            <input type="email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className={inputCls} placeholder="you@example.com" data-testid="contact-email-input" />
                        </div>
                        <div>
                            <label className="font-body text-white/70 text-sm font-semibold block mb-1">Message</label>
                            <textarea required value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} rows={5} className={inputCls} placeholder="Write your message here..." data-testid="contact-message-input" />
                        </div>
                        {error && <p className="text-red-400 text-sm font-body bg-red-900/30 border border-red-500/30 rounded p-3" data-testid="contact-error">{error}</p>}
                        {success && <p className="text-green-400 text-sm font-body bg-green-900/30 border border-green-500/30 rounded p-3" data-testid="contact-success">{success}</p>}
                        <button type="submit" disabled={sending} className="w-full bg-gold hover:bg-gold-hover disabled:opacity-50 text-navy font-bold py-4 rounded-sm font-body text-lg transition-colors min-h-[56px] flex items-center justify-center gap-2" data-testid="contact-submit-btn">
                            <Send size={18} /> {sending ? 'Sending...' : 'Send Message'}
                        </button>
                    </form>
                </div>
            </div>
        </section>
    );
}

// ─── REVIEWS SECTION ──────────────────────────────────────────────
// Replaces the former RSVP section. Approved customer reviews appear on
// the homepage; visitors can submit a new review which is held as
// "Pending" until an admin approves it from the admin panel.
function StarDisplay({ value, size = 'text-lg' }) {
    return (
        <span className={`text-gold ${size}`} aria-label={`${value} out of 5 stars`}>
            {'★'.repeat(value)}<span className="text-gold/30">{'★'.repeat(Math.max(0, 5 - value))}</span>
        </span>
    );
}

function StarPicker({ value, onChange }) {
    return (
        <div className="flex gap-1" data-testid="review-star-picker">
            {[1,2,3,4,5].map(n => (
                <button
                    key={n}
                    type="button"
                    onClick={() => onChange(n)}
                    className={`text-3xl leading-none transition-transform hover:scale-110 ${n <= value ? 'text-gold' : 'text-gold/30'}`}
                    aria-label={`Rate ${n} star${n>1?'s':''}`}
                    data-testid={`review-star-${n}`}
                >
                    ★
                </button>
            ))}
        </div>
    );
}

function ReviewsSection() {
    const [reviews, setReviews] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ full_name: '', rating: 0, review_text: '' });
    const [mediaFiles, setMediaFiles] = useState([]); // [{file, previewUrl, status, url?, error?}]
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [confirmation, setConfirmation] = useState('');
    const [apiError, setApiError] = useState('');
    const [lightbox, setLightbox] = useState(null);  // image URL when opened

    useEffect(() => {
        axios.get(`${API}/reviews`).then(r => setReviews(r.data || [])).catch(() => {});
    }, []);

    const handleMediaSelect = (e) => {
        const incoming = Array.from(e.target.files || []);
        if (!incoming.length) return;
        const ALLOWED_IMG = ['image/jpeg','image/jpg','image/png','image/webp'];
        const ALLOWED_VID = ['video/mp4','video/quicktime','video/mov','video/x-m4v'];
        const MAX = 50 * 1024 * 1024;
        const next = [...mediaFiles];
        for (const f of incoming) {
            if (next.length >= 5) break;
            const ct = (f.type || '').toLowerCase();
            if (!ALLOWED_IMG.includes(ct) && !ALLOWED_VID.includes(ct)) {
                alert(`${f.name}: only JPG, PNG, WEBP, MP4, or MOV allowed.`);
                continue;
            }
            if (f.size > MAX) { alert(`${f.name}: max 50MB.`); continue; }
            next.push({
                file: f,
                previewUrl: URL.createObjectURL(f),
                kind: ct.startsWith('video/') ? 'video' : 'image',
                status: 'pending',
            });
        }
        setMediaFiles(next);
        e.target.value = '';
    };

    const removeMedia = (idx) => {
        const f = mediaFiles[idx];
        if (f?.previewUrl) URL.revokeObjectURL(f.previewUrl);
        setMediaFiles(prev => prev.filter((_, i) => i !== idx));
    };

    const validate = () => {
        const e = {};
        if (!form.full_name.trim()) e.full_name = 'Please enter your name.';
        if (!form.rating || form.rating < 1 || form.rating > 5) e.rating = 'Please select a star rating.';
        if (!form.review_text.trim()) e.review_text = 'Please write a short review.';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = async (ev) => {
        ev.preventDefault();
        setApiError(''); setConfirmation('');
        if (!validate()) return;
        setSubmitting(true);
        try {
            // 1. Upload each media file → collect bunny URLs
            const uploadedUrls = [];
            for (let i = 0; i < mediaFiles.length; i++) {
                const m = mediaFiles[i];
                const fd = new FormData();
                fd.append('file', m.file);
                try {
                    const r = await axios.post(`${API}/reviews/upload-media`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                    uploadedUrls.push(r.data.url);
                    setMediaFiles(prev => prev.map((x, idx) => idx === i ? { ...x, status: 'uploaded', url: r.data.url } : x));
                } catch (uErr) {
                    setApiError(`Failed to upload ${m.file.name}: ${uErr.response?.data?.detail || 'unknown error'}`);
                    setSubmitting(false);
                    return; // do not submit a partial review
                }
            }
            // 2. Submit the review with the URLs
            await axios.post(`${API}/reviews`, {
                full_name: form.full_name.trim(),
                rating: Number(form.rating),
                review_text: form.review_text.trim(),
                media_urls: uploadedUrls,
            });
            setConfirmation('Thank you! Your review has been submitted and will appear once approved.');
            setForm({ full_name: '', rating: 0, review_text: '' });
            mediaFiles.forEach(m => m.previewUrl && URL.revokeObjectURL(m.previewUrl));
            setMediaFiles([]);
            setErrors({});
            setShowForm(false);
        } catch (err) {
            setApiError(err.response?.data?.detail || 'Could not submit review. Please try again.');
        } finally { setSubmitting(false); }
    };

    const resolveMediaUrl = (u) => (u?.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);
    const isVideo = (u) => /\.(mp4|mov|m4v)(\?|$)/i.test(u || '');

    const inputCls = 'w-full bg-white text-navy border border-gray-300 rounded-sm px-4 py-3 font-body text-base min-h-[44px] focus:outline-none focus:ring-2 focus:ring-gold';
    const labelCls = 'block text-left font-body text-sm text-navy/70 mb-1';

    return (
        <section id="reviews" className="bg-navy py-20 md:py-28" data-testid="reviews-section">
            <div className="max-w-5xl mx-auto px-6">
                <div className="text-center mb-12">
                    <h2 className="font-heading text-4xl md:text-5xl text-gold font-bold">Reader Reviews</h2>
                    <div className="w-20 h-1 bg-gold mx-auto mt-4"></div>
                </div>

                {reviews.length === 0 ? (
                    <div className="max-w-xl mx-auto bg-ivory rounded-sm p-8 text-center" data-testid="reviews-empty">
                        <p className="font-body text-navy text-lg mb-1">No reviews yet.</p>
                        <p className="font-body text-navy/70">Be the first to share your thoughts on <em>Know Your Stuff</em>.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="reviews-grid">
                        {reviews.map(r => (
                            <article key={r.review_id} className="bg-ivory rounded-sm p-6 shadow-lg shadow-gold/10 border border-gold/10" data-testid={`review-card-${r.review_id}`}>
                                <div className="flex items-center justify-between mb-3">
                                    <StarDisplay value={r.rating}/>
                                    <span className="font-body text-xs text-navy/50">{new Date(r.created_at).toLocaleDateString()}</span>
                                </div>
                                <p className="font-body text-navy text-base leading-relaxed mb-4 whitespace-pre-wrap">{r.review_text}</p>
                                {(r.media_urls || []).length > 0 && (
                                    <div className="grid grid-cols-3 gap-2 mb-4" data-testid={`review-media-${r.review_id}`}>
                                        {(r.media_urls || []).map((u, i) => {
                                            const src = resolveMediaUrl(u);
                                            return isVideo(u) ? (
                                                <video key={i} src={src} controls preload="metadata" className="w-full aspect-square bg-black object-contain rounded-sm border border-gold/20" data-testid={`review-video-${r.review_id}-${i}`}/>
                                            ) : (
                                                <button key={i} type="button" onClick={() => setLightbox(src)} className="block w-full aspect-square rounded-sm overflow-hidden border border-gold/20 hover:border-gold transition-colors" data-testid={`review-image-thumb-${r.review_id}-${i}`}>
                                                    <img src={src} alt={`Review photo ${i+1}`} className="w-full h-full object-cover" loading="lazy" decoding="async" width="180" height="180"/>
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                                <p className="font-heading text-navy font-semibold text-sm" data-testid={`review-author-${r.review_id}`}>— {r.full_name}</p>
                            </article>
                        ))}
                    </div>
                )}

                <div className="text-center mt-12">
                    {confirmation && (
                        <div className="max-w-md mx-auto bg-ivory text-navy px-6 py-6 rounded-sm font-body mb-6" data-testid="review-confirmation">
                            <p className="text-lg font-bold mb-1">Thank you!</p>
                            <p className="text-base">{confirmation}</p>
                        </div>
                    )}

                    {!showForm && (
                        <button
                            onClick={() => { setShowForm(true); setConfirmation(''); }}
                            className="bg-gold hover:bg-gold-hover text-navy font-bold px-10 py-4 rounded-sm font-body text-lg transition-colors min-h-[52px]"
                            data-testid="review-open-form-btn"
                        >
                            Leave a Review
                        </button>
                    )}

                    {showForm && (
                        <form onSubmit={handleSubmit} className="max-w-md mx-auto bg-ivory rounded-sm p-6 md:p-8 text-left space-y-4" data-testid="review-form" noValidate>
                            <div>
                                <label className={labelCls} htmlFor="review-name">Full Name</label>
                                <input id="review-name" type="text" value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })} className={inputCls} data-testid="review-name-input"/>
                                {errors.full_name && <p className="text-red-600 text-xs font-body mt-1" data-testid="review-name-error">{errors.full_name}</p>}
                            </div>
                            <div>
                                <label className={labelCls}>Rating</label>
                                <StarPicker value={form.rating} onChange={(n) => setForm({ ...form, rating: n })}/>
                                {errors.rating && <p className="text-red-600 text-xs font-body mt-1" data-testid="review-rating-error">{errors.rating}</p>}
                            </div>
                            <div>
                                <label className={labelCls} htmlFor="review-text">Your Review</label>
                                <textarea id="review-text" rows={4} value={form.review_text} onChange={e => setForm({ ...form, review_text: e.target.value })} className={inputCls} data-testid="review-text-input"/>
                                {errors.review_text && <p className="text-red-600 text-xs font-body mt-1" data-testid="review-text-error">{errors.review_text}</p>}
                            </div>
                            <div>
                                <label className={labelCls}>Add Photos or Videos (optional)</label>
                                <p className="font-body text-xs text-navy/60 mb-2">Up to 5 files, 50 MB each. Accepts JPG, PNG, WEBP, MP4, MOV.</p>
                                {mediaFiles.length < 5 && (
                                    <label className="inline-flex items-center gap-2 cursor-pointer bg-white border border-gold text-navy font-body text-sm font-semibold px-4 py-2 rounded-sm hover:bg-gold/10 transition-colors" data-testid="review-media-add-label">
                                        + Add Files
                                        <input type="file" accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime" multiple onChange={handleMediaSelect} className="hidden" data-testid="review-media-input"/>
                                    </label>
                                )}
                                {mediaFiles.length > 0 && (
                                    <div className="grid grid-cols-3 gap-2 mt-3" data-testid="review-media-previews">
                                        {mediaFiles.map((m, i) => (
                                            <div key={i} className="relative aspect-square rounded-sm overflow-hidden border border-navy/20 bg-white">
                                                {m.kind === 'video' ? (
                                                    <video src={m.previewUrl} className="w-full h-full object-cover" muted/>
                                                ) : (
                                                    <img src={m.previewUrl} alt="preview" className="w-full h-full object-cover"/>
                                                )}
                                                <button type="button" onClick={() => removeMedia(i)} className="absolute top-1 right-1 bg-navy/80 hover:bg-red-600 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center" data-testid={`review-media-remove-${i}`}>×</button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            {apiError && <p className="text-red-600 text-sm font-body" data-testid="review-api-error">{apiError}</p>}
                            <div className="flex gap-3 justify-end">
                                <button type="button" onClick={() => { setShowForm(false); setApiError(''); setErrors({}); }} className="px-5 py-3 font-body text-navy/70 hover:text-navy" data-testid="review-cancel-btn">Cancel</button>
                                <button type="submit" disabled={submitting} className="bg-gold hover:bg-gold-hover disabled:opacity-50 text-navy font-bold px-6 py-3 rounded-sm font-body transition-colors min-h-[48px]" data-testid="review-submit-btn">
                                    {submitting ? 'Submitting…' : 'Submit Review'}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>

            {/* Lightbox for full-size review photos */}
            {lightbox && (
                <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 cursor-zoom-out" onClick={() => setLightbox(null)} data-testid="review-lightbox">
                    <img src={lightbox} alt="Review" className="max-w-full max-h-full object-contain"/>
                </div>
            )}
        </section>
    );
}


function NewsletterSection() {
    const [email, setEmail] = useState('');
    const [sending, setSending] = useState(false);
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault(); setSending(true); setError(''); setSuccess('');
        try {
            const r = await axios.post(`${API}/newsletter/subscribe`, { email });
            setSuccess(r.data.message);
            setEmail('');
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to subscribe.');
        } finally { setSending(false); }
    };

    return (
        <section id="newsletter-section" className="bg-ivory py-16" data-testid="newsletter-section">
            <div className="max-w-2xl mx-auto px-6 text-center">
                <h2 className="font-heading text-3xl md:text-4xl text-navy font-bold mb-3">Stay in the Know</h2>
                <p className="font-body text-navy/60 mb-6">Get updates on new releases, events, and exclusive offers from Alice M. White.</p>
                <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto" data-testid="newsletter-form">
                    <input type="email" required value={email} onChange={e => setEmail(e.target.value)} className="flex-1 bg-white text-navy border border-gray-300 rounded-sm px-4 py-3 font-body text-base min-h-[44px] focus:outline-none focus:ring-2 focus:ring-gold" placeholder="Your email address" data-testid="newsletter-email-input"/>
                    <button type="submit" disabled={sending} className="bg-gold hover:bg-gold-hover disabled:opacity-50 text-navy font-bold px-6 py-3 rounded-sm font-body transition-colors min-h-[44px]" data-testid="newsletter-submit-btn">
                        {sending ? 'Subscribing...' : 'Subscribe'}
                    </button>
                </form>
                {success && <p className="text-green-700 text-sm font-body mt-3" data-testid="newsletter-success">{success}</p>}
                {error && <p className="text-red-600 text-sm font-body mt-3" data-testid="newsletter-error">{error}</p>}
            </div>
        </section>
    );
}

// ─── FOOTER ──────────────────────────────────────────────────────
function Footer() {
    const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    const [visitorCount, setVisitorCount] = useState(null);
    useEffect(() => {
        let cancelled = false;
        axios.get(`${API}/visits/count`)
            .then(r => { if (!cancelled) setVisitorCount(r.data.total); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, []);
    return (
        <footer className="bg-navy border-t border-gold/30 py-12" data-testid="footer">
            <div className="max-w-6xl mx-auto px-6 md:px-12">
                <div className="text-center space-y-6">
                    <button onClick={()=>scrollTo('hero')} className="font-heading text-gold text-2xl font-bold tracking-wide hover:opacity-80 transition-opacity">KNOW YOUR STUFF</button>
                    <p className="font-body text-white/60 italic">Physical & E-Book by Alice M. White, B.S., MBA</p>
                    <div className="flex flex-wrap justify-center gap-6">
                        {[['about-book','About the Book'],['about-author','About the Author'],['shop','Shop'],['contact','Contact'],['account','My Account']].map(([id,label])=>(
                            <button key={id} onClick={()=>scrollTo(id)} className="font-body text-white/60 hover:text-gold transition-colors text-base">{label}</button>
                        ))}
                    </div>
                    <div className="inline-flex flex-col items-center gap-1 px-6 py-3 border border-gold/30 rounded-sm" data-testid="visitor-counter">
                        <span className="font-heading text-gold text-3xl font-bold tabular-nums tracking-wider" data-testid="visitor-counter-number">
                            {visitorCount === null ? '—' : visitorCount.toLocaleString()}
                        </span>
                        <span className="font-body text-white/50 text-xs uppercase tracking-widest">Visitors</span>
                    </div>
                    <p className="font-body text-white/40 text-sm">Published by Lee's Press — A Premiere Self-Publishing Services Company</p>
                    <p className="font-body text-white/40 text-sm">&copy; 2026 Alice M. White. All Rights Reserved. | <span className="text-gold/50">knowyourstuffbook.com</span></p>
                </div>
            </div>
        </footer>
    );
}

// ─── SCROLL NEXT BUTTON ───────────────────────────────────────────
function ScrollNextButton() {
    const SECTIONS = ['hero', 'about-book', 'about-author', 'shop', 'account', 'contact', 'newsletter-section'];
    const [currentIdx, setCurrentIdx] = useState(0);
    const [atBottom, setAtBottom] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            const scrollY = window.scrollY;
            const windowH = window.innerHeight;
            const docH = document.documentElement.scrollHeight;
            const isBottom = scrollY + windowH >= docH - 300;
            setAtBottom(isBottom);
            if (!isBottom) {
                let found = 0;
                for (let i = SECTIONS.length - 1; i >= 0; i--) {
                    const el = document.getElementById(SECTIONS[i]);
                    if (el && el.getBoundingClientRect().top <= windowH * 0.4) {
                        found = i;
                        break;
                    }
                }
                setCurrentIdx(found);
            }
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll();
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleClick = () => {
        if (atBottom) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
            const nextIdx = Math.min(currentIdx + 1, SECTIONS.length - 1);
            document.getElementById(SECTIONS[nextIdx])?.scrollIntoView({ behavior: 'smooth' });
        }
    };

    return (
        <button
            onClick={handleClick}
            className="fixed bottom-8 right-8 z-40 bg-gold/90 hover:bg-gold text-navy rounded-full w-12 h-12 flex items-center justify-center shadow-lg shadow-navy/30 transition-all hover:scale-110"
            aria-label={atBottom ? 'Scroll to top' : 'Scroll to next section'}
            data-testid="scroll-next-btn"
        >
            <ChevronDown size={24} className={`transition-transform duration-300 ${atBottom ? 'rotate-180' : ''}`}/>
        </button>
    );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────
export default function HomePage({ scrollTarget }) {
    const { user, logout } = useAuth();
    const location = useLocation();

    const handleLoginClick = () => {
        document.getElementById('account')?.scrollIntoView({ behavior: 'smooth' });
    };

    // Track this visit once per page mount. The backend dedupes the same
    // IP within a 30-min window so reloads / SPA re-mounts don't inflate
    // the count.
    useEffect(() => {
        axios.post(`${API}/visits/track`).catch(() => {});
    }, []);

    // Auto-scroll to target section on mount (for SEO-friendly routes)
    useEffect(() => {
        if (scrollTarget) {
            const timer = setTimeout(() => {
                document.getElementById(scrollTarget)?.scrollIntoView({ behavior: 'smooth' });
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [scrollTarget]);

    // Get SEO meta for current route
    const seo = SEO_META[location.pathname] || SEO_META['/'];

    return (
        <div className="min-h-screen bg-navy">
            <Helmet>
                <title>{seo.title}</title>
                <meta name="description" content={seo.description} />
                <link rel="canonical" href={seo.canonical} />
            </Helmet>
            <Navbar user={user} onLogout={logout} onLoginClick={handleLoginClick}/>
            <HeroSection/>
            <AboutBookSection/>
            <AboutAuthorSection/>
            <ShopSection/>
            <MyAccountSection/>
            <ReviewsSection/>
            <ContactSection/>
            <NewsletterSection/>
            <Footer/>
            <ScrollNextButton/>
        </div>
    );
}
